import React, {useCallback} from 'react';

import BoolControl from './BoolControl';
import TextControl from './TextControl';
import RangeControl from './RangeControl';
import DateControl from './DateControl/DateControl';
import SearchControl from './SearchControl/SearchControl';

import {ISearchCondition} from '../../../types/filters';
import {ColumnType} from '../../../types/column';
import {Operator} from '@priority-software/priority-utils/lib/operators';
import {ISearchField} from '../../../../../common/searchType';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    dropdownWidth?: number;
    session: string;
    onUpdateFilter: (value: string[]) => void;
    onRemoveValue: (value: any) => void;
    scrollToBottom: () => void;
    onSelectClose?: () => void;
    onDateChange: (data: {date: string; dynamicDate: string}[]) => void;
}

const RANGE_TYPES = [ColumnType.Time, ColumnType.Duration, ColumnType.Number];

const Control = ({
    filter,
    column,
    session,
    dropdownWidth,
    onUpdateFilter,
    onRemoveValue,
    scrollToBottom,
    onDateChange,
    onSelectClose
}: Props) => {
    const {operator, type} = filter;
    const {isZoom} = column;

    const handleUpdate = useCallback(
        (value: string | string[]) => {
            if (Array.isArray(value)) {
                if (JSON.stringify(filter.values) !== JSON.stringify(value)) {
                    onUpdateFilter(value);
                }
            } else {
                if (type === ColumnType.Text) {
                    onUpdateFilter([...new Set([...filter.values, value])]);
                } else {
                    onUpdateFilter([value]);
                }
            }
        },
        [type, onUpdateFilter, filter.values]
    );

    if (
        isZoom &&
        type === ColumnType.Text &&
        (operator === Operator.Equals || operator === Operator.NotEquals)
    ) {
        return (
            <SearchControl
                dropdownWidth={dropdownWidth}
                scrollToBottom={scrollToBottom}
                session={session}
                filter={filter}
                column={column}
                onRemoveValue={onRemoveValue}
                onSelect={handleUpdate}
                onClose={onSelectClose}
            />
        );
    }

    if (type === ColumnType.Bool) {
        return <BoolControl onChange={handleUpdate} filter={filter} />;
    }

    const isDisabled = Operator.Empty === operator || Operator.NotEmpty === operator;

    if (type === ColumnType.Date) {
        return (
            <DateControl
                column={column}
                isDisabled={isDisabled}
                filter={filter}
                onUpdate={onDateChange}
            />
        );
    }

    if (RANGE_TYPES.includes(type) || operator === Operator.Between) {
        return (
            <RangeControl
                column={column}
                isDisabled={isDisabled}
                filter={filter}
                onUpdate={handleUpdate}
            />
        );
    }

    return <TextControl isDisabled={isDisabled} onUpdateFilter={handleUpdate} />;
};

export default Control;
